import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useHeaderContext } from '~/contexts/HeaderContext';
import { inMobile, inTablet, useWindowDimensions } from '~/hooks/useDimensions';
import { hasRenderableContent } from '~/utils/filters';

import {
  ActionsSection,
  DesktopContainer,
  FiltersLabel,
  FiltersSection,
  MobileContainer,
  SearchSection,
  SubHeaderContainer,
} from './SubHeader.styles';

interface SubHeaderProps {
  leftItems?: ReactNode; // Filters, dropdowns (always left side)
  rightItems?: ReactNode; // Action buttons for mobile
  searchItems?: ReactNode; // Search inputs (always right side)
  children?: ReactNode;
  hideOnDesktop?: boolean;
  hideOnMobile?: boolean;
}

const SubHeader = ({
  leftItems: propLeftItems,
  rightItems: propRightItems,
  searchItems: propSearchItems,
  children,
  hideOnDesktop = false,
  hideOnMobile = false,
}: SubHeaderProps) => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const isMobile = inMobile(width);
  const isTablet = inTablet(width);

  const { subHeaderLeftItems, subHeaderRightItems, subHeaderSearchItems, disableAnimation } = useHeaderContext();

  // Use prop if provided, otherwise context
  const leftItems = propLeftItems ?? subHeaderLeftItems;
  const rightItems = propRightItems ?? subHeaderRightItems;
  const searchItems = propSearchItems ?? subHeaderSearchItems;

  const hasLeftItems = hasRenderableContent(leftItems);
  const hasRightItems = hasRenderableContent(rightItems);
  const hasSearchItems = hasRenderableContent(searchItems);

  // Only render if there is at least one item
  if (!hasLeftItems && !hasSearchItems && !hasRightItems) {
    return null;
  }

  if ((hideOnDesktop && !isMobile) || (hideOnMobile && isMobile)) {
    return null;
  }

  // On mobile/tablet, show filters on left, search and buttons on right
  if (isMobile || isTablet) {
    return (
      <SubHeaderContainer>
        <MobileContainer>
          {hasLeftItems ? <FiltersSection>{leftItems}</FiltersSection> : <div />}
          {(hasSearchItems || hasRightItems) && (
            <ActionsSection>
              {searchItems}
              {rightItems}
            </ActionsSection>
          )}
        </MobileContainer>
        {children}
      </SubHeaderContainer>
    );
  }

  // On desktop, show filters on left, search on right, and actions on far right
  // Only render sections that have content
  return (
    <SubHeaderContainer $animated={!disableAnimation}>
      <DesktopContainer>
        {hasLeftItems && (
          <FiltersSection>
            <FiltersLabel>{t('common.filters')}</FiltersLabel> {leftItems}
          </FiltersSection>
        )}
        {hasRightItems && <ActionsSection>{rightItems}</ActionsSection>}
        {hasSearchItems && <SearchSection>{searchItems}</SearchSection>}
        {children}
      </DesktopContainer>
    </SubHeaderContainer>
  );
};

export default SubHeader;
